
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { type BackupResult } from "@/utils/wordpressApi";
import { getBackupsFromSupabase } from "@/services/supabaseBackupService";
import { getPluginRegistration, checkPluginBackupStatus } from "@/services/wordpressPluginService";

export const useBackupHistory = (websiteId?: string) => {
  const { websiteId: paramWebsiteId } = useParams<{ websiteId: string }>();
  const actualWebsiteId = websiteId || paramWebsiteId;
  
  const [backups, setBackups] = useState<BackupResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState<string | null>(null);

  // Mock website data - in a real app, you'd fetch this from Supabase
  const websiteTitle = actualWebsiteId === "4" ? "Drive Expert" : "My WordPress Blog";

  const loadBackups = async () => {
    if (!actualWebsiteId) {
      setError("No website ID provided");
      setIsLoading(false);
      return;
    }

    try {
      console.log(`🔄 Loading backups for website ${actualWebsiteId} from Supabase`);
      setIsLoading(true);
      setError(null);
      
      const backupData = await getBackupsFromSupabase(actualWebsiteId);
      console.log(`📊 Loaded ${backupData.length} backups from Supabase`);
      
      // Check if we have plugin registration to update backup statuses
      const pluginRegistration = getPluginRegistration(actualWebsiteId);
      
      if (pluginRegistration) {
        // Update status for in-progress backups
        const updatedBackups = await Promise.all(
          backupData.map(async (backup) => {
            if (backup.status === 'in-progress') {
              try {
                console.log(`🔍 Checking status for in-progress backup ${backup.id}`);
                const updatedStatus = await checkPluginBackupStatus(actualWebsiteId, backup.id);
                if (updatedStatus) {
                  console.log(`✅ Status updated for backup ${backup.id}:`, updatedStatus);
                  return { ...backup, ...updatedStatus };
                }
              } catch (error) {
                console.error(`Error checking status for backup ${backup.id}:`, error);
              }
            }
            return backup;
          })
        );
        setBackups(updatedBackups);
      } else {
        setBackups(backupData);
      }
      
      if (backupData.length === 0) {
        console.log(`📊 No backups found for website ${actualWebsiteId}`);
      }
    } catch (err) {
      console.error("Error loading backups:", err);
      setError("Failed to load backup history from Supabase");
      
      toast({
        title: "Error loading backups",
        description: "Failed to load backup history from Supabase. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-refresh backups every 8 seconds if there are in-progress backups
  useEffect(() => {
    if (!actualWebsiteId) return;

    const hasInProgressBackups = backups.some(backup => backup.status === 'in-progress');
    
    if (hasInProgressBackups) {
      console.log(`🔄 Setting up auto-refresh for in-progress backups`);
      const interval = setInterval(() => {
        console.log(`🔄 Auto-refreshing backups due to in-progress status`);
        loadBackups();
      }, 8000); // Check every 8 seconds

      return () => {
        console.log(`🛑 Clearing auto-refresh interval`);
        clearInterval(interval);
      };
    }
  }, [backups, actualWebsiteId]);

  useEffect(() => {
    loadBackups();
  }, [actualWebsiteId]);

  const handleDeleteBackup = async (backupId: string) => {
    if (!actualWebsiteId) return;
    
    setDeleteInProgress(backupId);
    
    try {
      console.log(`🗑️ Deleting backup ${backupId}`);
      
      // Import and use deleteBackupFromSupabase
      const { deleteBackupFromSupabase } = await import("@/services/supabaseBackupService");
      const success = await deleteBackupFromSupabase(actualWebsiteId, backupId);
      
      if (success) {
        // Remove from local state
        setBackups(prev => prev.filter(backup => backup.id !== backupId));
      } else {
        throw new Error("Failed to delete backup");
      }
    } catch (error) {
      console.error("Error deleting backup:", error);
      
      toast({
        title: "Delete failed",
        description: "There was an error deleting your backup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteInProgress(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const refreshBackups = () => {
    console.log(`🔄 Manual refresh triggered`);
    loadBackups();
  };

  return {
    backups,
    isLoading,
    error,
    websiteTitle,
    deleteInProgress,
    handleDeleteBackup,
    formatDate,
    refreshBackups,
  };
};
